import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, FileText, ClipboardSignature, Briefcase,
  Receipt, FolderKanban, CalendarCheck, Activity, ListTodo, NotebookPen,
  Building2, LogOut, ChevronRight, BookOpen, Settings as SettingsIcon, FileBarChart, ScrollText,
  CalendarDays, Leaf, Factory, Wrench, Boxes, UserCircle2, KeyRound, Moon, Sun, ChevronDown,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { ROLE_LABELS } from "../lib/constants";
import { NotificationsBell } from "./NotificationsBell";
import { GlobalSearch } from "./GlobalSearch";
import { ChangePasswordModal } from "./ChangePasswordModal";
import { toast } from "sonner";

const NAV_GROUPS = [
  {
    title: "Overview",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, perm: "team_any" },
      { to: "/calendar", label: "Calendar", icon: CalendarDays, perm: "team_any" },
    ],
  },
  {
    title: "Sales",
    items: [
      { to: "/clients", label: "Client Management", icon: Building2, perm: "team_any" },
      { to: "/leads", label: "Lead Management", icon: Users, perm: "leads" },
      { to: "/proposals", label: "Proposals & Quotations", icon: FileText, perm: "proposals" },
      { to: "/work-orders", label: "Work Orders", icon: ClipboardSignature, perm: "work_orders" },
    ],
  },
  {
    title: "Delivery",
    items: [
      { to: "/projects", label: "Project Management", icon: Briefcase, perm: "projects" },
      { to: "/compliance", label: "EC & MPCB Compliance", icon: CalendarCheck, perm: "compliance" },
      { to: "/monitoring", label: "Monitoring", icon: Activity, perm: "compliance" },
      { to: "/trackers/pmr", label: "PMR Management", icon: Leaf, perm: "projects" },
      { to: "/trackers/mep", label: "MEP Coordination", icon: Wrench, perm: "projects" },
      { to: "/trackers/systems", label: "STP / OWC / AQMS / ASG", icon: Factory, perm: "projects" },
    ],
  },
  {
    title: "Collaboration",
    items: [
      { to: "/tasks", label: "Task Management", icon: ListTodo, perm: "tasks" },
      { to: "/dar", label: "DAR", icon: NotebookPen, perm: "team_any" },
      { to: "/mom", label: "MOM", icon: FolderKanban, perm: "team_any" },
      { to: "/repository", label: "Document Centre", icon: BookOpen, perm: "team_any" },
    ],
  },
  {
    title: "Administration",
    items: [
      { to: "/invoices", label: "Billing", icon: Receipt, perm: "invoices" },
      { to: "/reports", label: "MIS & Reports", icon: FileBarChart, perm: "reports" },
      { to: "/team", label: "User Management", icon: Boxes, perm: "team" },
      { to: "/audit", label: "Audit Log", icon: ScrollText, roles: ["super_admin", "hod", "director"] },
      { to: "/settings", label: "Settings", icon: SettingsIcon, roles: ["super_admin"] },
    ],
  },
];

function canSee(item, user) {
  if (!user) return false;
  if (item.roles) return item.roles.includes(user.role);
  if (user.role === "client") return false;
  if (user.role === "super_admin") return true;
  if (item.perm === "team_any") return true;
  return !!user.perms?.[item.perm]?.view;
}

const IDLE_LIMIT_MS = 30 * 60 * 1000;

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [showChangePwd, setShowChangePwd] = React.useState(false);
  const [theme, setTheme] = React.useState(() => localStorage.getItem("ecmp_theme") || "light");
  const profileRef = React.useRef(null);
  const idleTimer = React.useRef(null);

  React.useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("ecmp_theme", theme);
  }, [theme]);

  React.useEffect(() => {
    const onClick = (e) => { if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Session security: auto-logout after 30 min inactivity
  React.useEffect(() => {
    const reset = () => {
      clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(async () => {
        toast.info("Session expired due to inactivity — please log in again");
        await logout();
        nav("/login");
      }, IDLE_LIMIT_MS);
    };
    ["mousemove", "keydown", "click", "scroll", "touchstart"].forEach((ev) => window.addEventListener(ev, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(idleTimer.current);
      ["mousemove", "keydown", "click", "scroll", "touchstart"].forEach((ev) => window.removeEventListener(ev, reset));
    };
  }, [logout, nav]);

  const initials = user?.name?.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "U";

  return (
    <div className="min-h-screen flex bg-stone-50" style={{ fontFamily: "Poppins, sans-serif" }}>
      {/* Sidebar — PTESPL dark green */}
      <aside className="w-64 text-white/70 flex flex-col fixed h-screen z-40" style={{ background: "#145353" }} data-testid="sidebar">
        <div className="px-5 h-16 flex items-center gap-3 border-b border-white/10 shrink-0">
          <img src="/ptespl-mark-white.png" alt="PT" className="h-10 w-auto" data-testid="sidebar-logo" />
          <div>
            <div className="text-white font-bold text-sm tracking-tight leading-tight">ECMP CRM</div>
            <div className="text-[8.5px] uppercase tracking-[0.14em]" style={{ color: "#72BE44" }}>PT Ecological Services</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {NAV_GROUPS.map((g) => {
            const items = g.items.filter((n) => canSee(n, user));
            if (!items.length) return null;
            return (
              <div key={g.title} className="mb-3">
                <div className="px-3 pb-1 text-[9px] uppercase tracking-[0.2em] text-white/35 font-bold">{g.title}</div>
                {items.map((item) => {
                  const active = loc.pathname === item.to || (item.to !== "/" && loc.pathname.startsWith(item.to));
                  const Icon = item.icon;
                  return (
                    <Link key={item.to} to={item.to}
                      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`group flex items-center gap-2.5 px-3 py-[7px] text-[13px] rounded-md transition-all duration-200 ${
                        active ? "text-white border-l-2 pl-[10px]" : "hover:bg-white/5 hover:text-white"}`}
                      style={active ? { background: "rgba(114, 190, 68, 0.15)", borderColor: "#72BE44" } : {}}>
                      <Icon className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                      <span className="font-medium truncate">{item.label}</span>
                      {active && <ChevronRight className="w-3 h-3 ml-auto opacity-60 shrink-0" />}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div className="border-t border-white/10 p-3 shrink-0">
          <div className="px-2 text-[9px] text-white/40 text-center">Environmental Compliance<br />Managing Partner</div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64 flex flex-col min-w-0">
        <header className="h-16 bg-white/80 backdrop-blur-md border-b border-stone-200 sticky top-0 z-30 flex items-center px-6 gap-4">
          <GlobalSearch />
          <div className="flex items-center gap-1.5 ml-auto">
            <button onClick={() => setTheme(theme === "light" ? "dark" : "light")} data-testid="theme-toggle"
              className="p-2 rounded-lg hover:bg-stone-100 transition" title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}>
              {theme === "light" ? <Moon className="w-4 h-4" style={{ color: "#145353" }} /> : <Sun className="w-4 h-4 text-amber-400" />}
            </button>
            <NotificationsBell />
            <div className="relative" ref={profileRef}>
              <button onClick={() => setProfileOpen(!profileOpen)} data-testid="profile-menu-btn"
                className="flex items-center gap-2 pl-1.5 pr-2 py-1.5 rounded-lg hover:bg-stone-100 transition">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0" style={{ background: "#72BE44" }}>{initials}</div>
                <div className="text-left hidden md:block">
                  <div className="text-xs font-semibold text-stone-800 leading-tight max-w-[120px] truncate" data-testid="user-name">{user?.name}</div>
                  <div className="text-[9px] uppercase tracking-wider text-stone-400">{ROLE_LABELS[user?.role] || user?.role}</div>
                </div>
                <ChevronDown className="w-3 h-3 text-stone-400" />
              </button>
              {profileOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-stone-200 rounded-xl shadow-xl z-50 overflow-hidden py-1" data-testid="profile-dropdown">
                  <div className="px-4 py-2.5 border-b border-stone-100">
                    <div className="text-sm font-semibold text-stone-900 truncate">{user?.name}</div>
                    <div className="text-xs text-stone-400 truncate">{user?.email}</div>
                  </div>
                  <button onClick={() => { setProfileOpen(false); nav("/profile"); }} data-testid="menu-profile"
                    className="w-full text-left px-4 py-2.5 text-sm text-stone-700 hover:bg-stone-50 flex items-center gap-2.5">
                    <UserCircle2 className="w-4 h-4" style={{ color: "#145353" }} /> My Profile
                  </button>
                  <button onClick={() => { setProfileOpen(false); setShowChangePwd(true); }} data-testid="menu-change-password"
                    className="w-full text-left px-4 py-2.5 text-sm text-stone-700 hover:bg-stone-50 flex items-center gap-2.5">
                    <KeyRound className="w-4 h-4" style={{ color: "#145353" }} /> Change Password
                  </button>
                  <button onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-btn"
                    className="w-full text-left px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2.5 border-t border-stone-100">
                    <LogOut className="w-4 h-4" /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-8">{children}</main>
        <footer className="border-t border-stone-200 bg-white px-8 py-3 text-xs text-stone-500 flex items-center justify-between" data-testid="app-footer">
          <span><b style={{ color: "#145353" }}>PTESPL</b> — Environmental Compliance Managing Partner (ECMP)</span>
          <span>© {new Date().getFullYear()} PT Ecological Services Pvt. Ltd.</span>
        </footer>
      </div>

      {showChangePwd && <ChangePasswordModal onClose={() => setShowChangePwd(false)} />}
    </div>
  );
}
